import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import VideocamIcon from '@material-ui/icons/Videocam';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import { Box } from '@material-ui/core';
import { CameraTile } from '@vgps/livekit_player';
import {
  DEFAULT_LIVE_QUALITY,
  getLiveQualityStorageKey,
  primeDefaultLiveQuality
} from 'src/app/sdk/livekit/qualityDefaults';

const STREAM_TILE_VIDEO_EVENTS = [
  'loadedmetadata',
  'canplay',
  'play',
  'pause',
  'volumechange',
  'emptied'
];

const removeBoundVideoEvents = (videoEl, syncVideoState) => {
  if (!videoEl) return;

  STREAM_TILE_VIDEO_EVENTS.forEach(eventName => {
    videoEl.removeEventListener(eventName, syncVideoState);
  });
  delete videoEl.__cmsControlsBound;
};

const useStreamTileState = tileRef => {
  const videoElementRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [volume, setVolume] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const tileEl = tileRef.current;
    if (!tileEl) return undefined;

    let videoEl = null;
    let pollTimer = null;

    const syncVideoState = () => {
      const nextVideo = tileEl.querySelector('video');
      const canvasEl = tileEl.querySelector('canvas');

      if (videoElementRef.current && videoElementRef.current !== nextVideo) {
        removeBoundVideoEvents(videoElementRef.current, syncVideoState);
      }

      videoEl = nextVideo;
      videoElementRef.current = nextVideo;

      if (!nextVideo && !canvasEl) {
        setVolume(100);
        setIsMuted(true);
        setIsLive(false);
        return;
      }

      if (nextVideo) {
        const nextVolume = Math.round((nextVideo.volume ?? 1) * 100);
        setVolume(nextVolume);
        setIsMuted(Boolean(nextVideo.muted || nextVolume === 0));
      }

      const hasFrames =
        (nextVideo &&
          ((nextVideo.srcObject && nextVideo.readyState >= 2) ||
            nextVideo.readyState >= 3)) ||
        (canvasEl && (canvasEl.width > 0 || canvasEl.clientWidth > 0));

      setIsLive(Boolean(hasFrames && !nextVideo?.ended));
    };

    const bindVideoEvents = () => {
      if (!videoEl || videoEl.__cmsControlsBound) return;

      videoEl.__cmsControlsBound = true;
      STREAM_TILE_VIDEO_EVENTS.forEach(eventName => {
        videoEl.addEventListener(eventName, syncVideoState);
      });
    };

    const observer = new MutationObserver(() => {
      syncVideoState();
      bindVideoEvents();
    });

    observer.observe(tileEl, {
      childList: true,
      subtree: true,
      attributes: true
    });

    syncVideoState();
    bindVideoEvents();
    pollTimer = window.setInterval(syncVideoState, 1500);

    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === tileEl);
    };

    document.addEventListener('fullscreenchange', onFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      observer.disconnect();
      if (pollTimer) window.clearInterval(pollTimer);
      removeBoundVideoEvents(videoElementRef.current, syncVideoState);
    };
  }, [tileRef]);

  return {
    isHovered,
    isLive,
    isMuted,
    volume,
    isFullscreen,
    setIsHovered,
    setIsMuted
  };
};

const StreamTileToolbar = ({
  isVisible,
  isMuted,
  isFullscreen,
  onToggleMute,
  onScreenshot,
  onToggleFullscreen
}) => (
  <Box
    className={`cms-camera-tile-toolbar ${
      isVisible ? 'cms-camera-tile-toolbar-visible' : ''
    }`}
  >
    <Box className="cms-camera-toolbar-group">
      <Box
        component="button"
        type="button"
        className="cms-camera-tool-button"
        onClick={onToggleMute}
        aria-label={isMuted ? 'Bật âm thanh' : 'Tắt âm thanh'}
        title={isMuted ? 'Bật âm thanh' : 'Tắt âm thanh'}
      >
        {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
      </Box>
    </Box>
    <Box className="cms-camera-toolbar-group">
      <Box
        component="button"
        type="button"
        className="cms-camera-tool-button"
        onClick={onScreenshot}
        aria-label="Chụp ảnh camera"
        title="Chụp ảnh"
      >
        <PhotoCameraIcon />
      </Box>
      <Box
        component="button"
        type="button"
        className="cms-camera-tool-button"
        onClick={onToggleFullscreen}
        aria-label={isFullscreen ? 'Thoát toàn màn hình camera' : 'Mở toàn màn hình camera'}
        title={isFullscreen ? 'Thoát toàn màn hình' : 'Toàn màn hình'}
      >
        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </Box>
    </Box>
  </Box>
);

const StreamTile = ({
  slot,
  isFocused,
  isDropTarget,
  onFocus,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd
}) => {
  const tileRef = useRef(null);
  const slotDeviceSerial = slot?.deviceSerial;
  const slotChannel = String(slot?.channel || '');
  const qualityStorageKey = getLiveQualityStorageKey(
    slotDeviceSerial,
    slotChannel
  );
  const [primedQualityKey, setPrimedQualityKey] = useState('');
  const {
    isHovered,
    isLive,
    isMuted,
    volume,
    isFullscreen,
    setIsHovered,
    setIsMuted
  } = useStreamTileState(tileRef);

  useLayoutEffect(() => {
    if (!slotDeviceSerial || !slotChannel) {
      return;
    }

    primeDefaultLiveQuality(slotDeviceSerial, slotChannel, DEFAULT_LIVE_QUALITY);
    setPrimedQualityKey(qualityStorageKey);
  }, [qualityStorageKey, slotChannel, slotDeviceSerial]);

  const handleToggleMute = useCallback(
    event => {
      event.stopPropagation();
      const videoEl = tileRef.current?.querySelector('video');
      if (!videoEl) return;

      const nextMuted = !(videoEl.muted || videoEl.volume === 0);
      videoEl.muted = nextMuted;
      if (!nextMuted && videoEl.volume === 0) {
        videoEl.volume = Math.max(volume / 100, 0.5);
      }
      setIsMuted(nextMuted);
    },
    [setIsMuted, volume]
  );

  const handleScreenshot = useCallback(
    event => {
      event.stopPropagation();
      const tileEl = tileRef.current;
      if (!tileEl) return;

      const mediaEl = tileEl.querySelector('video, canvas');
      if (!mediaEl) return;

      const canvas = document.createElement('canvas');
      const width = mediaEl.videoWidth || mediaEl.width || mediaEl.clientWidth;
      const height = mediaEl.videoHeight || mediaEl.height || mediaEl.clientHeight;

      if (!width || !height) return;

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.drawImage(mediaEl, 0, 0, width, height);

      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${slot.deviceSerial}_camera_${slot.channel}_${Date.now()}.png`;
      link.click();
    },
    [slot]
  );

  const handleToggleFullscreen = useCallback(
    async event => {
      event.stopPropagation();
      const tileEl = tileRef.current;
      if (!tileEl) return;

      if (document.fullscreenElement === tileEl) {
        await document.exitFullscreen?.();
        return;
      }

      await tileEl.requestFullscreen?.();
    },
    []
  );

  if (!slot) {
    return (
      <button
        type="button"
        className={`cms-camera-tile cms-camera-placeholder ${isFocused ? 'focused' : ''} ${isDropTarget ? 'drag-over' : ''}`}
        onClick={onFocus}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <VideocamIcon />
        <span>Chọn camera để thêm vào khung</span>
      </button>
    );
  }

  return (
    <div
      ref={tileRef}
      className={`cms-camera-tile cms-camera-stream-tile ${isLive ? 'live' : ''} ${isFocused ? 'focused' : ''} ${isDropTarget ? 'drag-over' : ''}`}
      onClick={onFocus}
      role="button"
      tabIndex={0}
      draggable={true}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onFocus();
        }
      }}
    >
      <div className="cms-camera-tile-status">
        <span>{slot.vehicle.license_plate || slot.vehicle.device_serial}</span>
      </div>
      {primedQualityKey === qualityStorageKey && (
        <CameraTile
          deviceSerial={slot.deviceSerial}
          channelId={String(slot.channel)}
          title={`CH${slot.channel}`}
          height="100%"
          initialQuality={DEFAULT_LIVE_QUALITY}
          pauseWhenHidden
        />
      )}
      <StreamTileToolbar
        isVisible={isLive && isHovered}
        isMuted={isMuted}
        isFullscreen={isFullscreen}
        onToggleMute={handleToggleMute}
        onScreenshot={handleScreenshot}
        onToggleFullscreen={handleToggleFullscreen}
      />
    </div>
  );
};

export default React.memo(StreamTile);
